import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { auth } from '@/config/firebase';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button";
import { Loader2, AlertTriangle, FileText, Database, Trash2 } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";

interface DeleteStartupDialogProps {
    startupIds: string[];
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSuccess: () => void;
}

export const DeleteStartupDialog = ({ startupIds, open, onOpenChange, onSuccess }: DeleteStartupDialogProps) => {
    const { toast } = useToast();
    const isBulk = startupIds.length > 1;
    const singleId = startupIds.length === 1 ? startupIds[0] : null;

    // Fetch startup details ONLY if single ID
    const { data: startup, isLoading, isError } = useQuery({
        queryKey: ['admin-startup-details', singleId],
        queryFn: async () => {
            if (!singleId) return null;
            const currentUser = auth.currentUser;
            if (!currentUser) throw new Error("No authenticated user");
            const token = await currentUser.getIdToken();
            const response = await axios.get(`${API_BASE_URL}/api/admin/startups/${singleId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        },
        enabled: open && !!singleId,
    });

    const deleteMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            const currentUser = auth.currentUser;
            if (!currentUser) throw new Error("No authenticated user");
            const token = await currentUser.getIdToken();

            if (ids.length === 1) {
                await axios.delete(`${API_BASE_URL}/api/admin/startups/${ids[0]}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            } else {
                await axios.post(`${API_BASE_URL}/api/admin/startups/bulk-delete`,
                    { startup_ids: ids },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            }
        },
        onSuccess: () => {
            toast({
                title: "Deletion Successful",
                description: isBulk
                    ? `${startupIds.length} startups have been deleted.`
                    : "Startup and associated data permanently removed."
            });
            onOpenChange(false);
            onSuccess();
        },
        onError: (error) => {
            toast({
                title: "Deletion Failed",
                description: "Could not complete deletion. Check logs.",
                variant: "destructive"
            });
        }
    });

    const documents = startup?.profile?.documents || [];

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle className="text-destructive flex items-center gap-2">
                        <Trash2 className="h-5 w-5" />
                        {isBulk ? `Delete ${startupIds.length} Startups?` : "Hard Delete Startup"}
                    </DialogTitle>
                    <DialogDescription>
                        This action is <strong>irreversible</strong>.
                        {isBulk
                            ? " These startups and all their associated data will be permanently removed."
                            : " It will permanently remove the startup and all associated data."
                        }
                    </DialogDescription>
                </DialogHeader>

                {isBulk ? (
                    <Alert variant="destructive" className="bg-destructive/10 border-destructive/20 text-destructive dark:text-red-400">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertTitle>Warning: Bulk Deletion</AlertTitle>
                        <AlertDescription>
                            You are about to permanently delete <strong>{startupIds.length}</strong> startups.
                            <br />
                            Confirm that you want to proceed.
                        </AlertDescription>
                    </Alert>
                ) : (
                    <>
                        {isLoading ? (
                            <div className="flex justify-center p-8">
                                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                            </div>
                        ) : isError ? (
                            <Alert variant="destructive">
                                <AlertTriangle className="h-4 w-4" />
                                <AlertTitle>Error</AlertTitle>
                                <AlertDescription>Failed to load startup details.</AlertDescription>
                            </Alert>
                        ) : startup ? (
                            <div className="space-y-4 py-2">
                                <Alert variant="destructive" className="bg-destructive/10 border-destructive/20 text-destructive dark:text-red-400">
                                    <AlertTriangle className="h-4 w-4" />
                                    <AlertTitle>Warning: Permanent Data Loss</AlertTitle>
                                    <AlertDescription>
                                        You are about to delete <strong>{startup.name}</strong> (ID: {startup.id}).
                                    </AlertDescription>
                                </Alert>

                                <div className="grid gap-2 text-sm">
                                    <h3 className="font-semibold flex items-center gap-2">
                                        <Database className="h-4 w-4 text-muted-foreground" />
                                        Database Records
                                    </h3>
                                    <ul className="list-disc pl-9 text-muted-foreground">
                                        <li>Startup Profile & Metadata</li>
                                        <li>Investor Statuses & Notes</li>
                                        <li>Analyses Records (BigQuery)</li>
                                        <li>Activity Feed & Questions (Firestore)</li>
                                    </ul>
                                </div>

                                <div className="grid gap-2">
                                    <h3 className="font-semibold text-sm flex items-center gap-2">
                                        <FileText className="h-4 w-4 text-muted-foreground" />
                                        Associated Files ({documents.length})
                                    </h3>
                                    <ScrollArea className="h-32 rounded-md border p-2">
                                        {documents.length > 0 ? (
                                            <ul className="space-y-1">
                                                {documents.map((doc: any, i: number) => (
                                                    <li key={i} className="text-xs text-muted-foreground truncate">
                                                        {doc.filename || doc.gcs_path.split('/').pop()}
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p className="text-xs text-muted-foreground italic">No documents found.</p>
                                        )}
                                    </ScrollArea>
                                </div>
                            </div>
                        ) : null}
                    </>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={deleteMutation.isPending}>
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={() => deleteMutation.mutate(startupIds)}
                        disabled={deleteMutation.isPending || (isLoading && !isBulk)}
                    >
                        {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {isBulk ? "Delete All Selected" : "Permanently Delete"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
